/**
 * The outbox itself: enqueue, list, replay, resolve.
 *
 * Design rules that are not negotiable:
 *
 *  1. ONE IDEMPOTENCY KEY PER OPERATION, FOR ITS WHOLE LIFE. Generated at
 *     enqueue, reused on every retry. The API stores the first response against
 *     that key and replays it verbatim, so a retry after a lost response is
 *     harmless. Generating a new key per attempt is exactly how a guest gets
 *     charged twice.
 *  2. FIFO WITHIN A DEVICE. Orders are replayed oldest first, because "gọi
 *     thêm món" on an order that has not been created yet is a 404. The
 *     replayer stops the run on the first retryable failure rather than
 *     skipping ahead.
 *  3. NOTHING IS EVER SILENTLY DROPPED. A permanent rejection becomes
 *     `CONFLICT` and stays visible until a human resolves it.
 *  4. THE POLICY IS ENFORCED HERE, not at the call sites. `enqueue()` calls
 *     `assertQueueable()`; a screen cannot opt a refund into the queue.
 */

import { ulid } from '@sonauto/utilities';
import { openOfflineDb, OUTBOX_STORE, META_STORE, isIndexedDbAvailable } from './db.js';
import { assertQueueable } from './policy.js';
import type {
  EnqueueInput,
  OutboxRecord,
  OutboxStatus,
  OutboxTransport,
  SyncSnapshot,
  SyncState,
} from './types.js';

const LAST_SYNCED_KEY = 'lastSyncedAt';

/** Backoff schedule in ms: 2s, 5s, 15s, 45s, 2m, then 5m forever. */
const BACKOFF_MS: readonly number[] = [2_000, 5_000, 15_000, 45_000, 120_000];
const MAX_BACKOFF_MS = 300_000;

function backoffFor(attempts: number): number {
  return BACKOFF_MS[attempts] ?? MAX_BACKOFF_MS;
}

/**
 * A UUID that works without a secure context. A POS on a plain-HTTP LAN has no
 * `crypto.randomUUID`, and falling straight to `Math.random` would risk a
 * collision on the very key that guarantees we do not double-charge.
 */
export function newClientUuid(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (c !== undefined && typeof c.randomUUID === 'function') return c.randomUUID();
  if (c !== undefined && typeof c.getRandomValues === 'function') {
    const bytes = c.getRandomValues(new Uint8Array(16));
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  // Last resort: a ULID is still monotonic and 80 bits of entropy.
  return ulid();
}

/** Put an operation in the queue. Throws if the policy forbids it. */
export async function enqueue(input: EnqueueInput): Promise<OutboxRecord> {
  assertQueueable(input.kind);
  const now = Date.now();
  const id = input.id ?? newClientUuid();
  const record: OutboxRecord = {
    id,
    idempotencyKey: input.idempotencyKey ?? id,
    kind: input.kind,
    request: input.request,
    status: 'PENDING',
    createdAt: now,
    updatedAt: now,
    attempts: 0,
    nextAttemptAt: now,
    lastError: null,
    lastStatus: null,
    branchId: input.branchId ?? null,
    label: input.label,
  };
  const db = await openOfflineDb();
  await db.put(OUTBOX_STORE, record);
  return record;
}

/** Everything in the queue, oldest first. */
export async function listAll(): Promise<OutboxRecord[]> {
  const db = await openOfflineDb();
  const all = await db.getAllFromIndex(OUTBOX_STORE, 'by_created');
  return all;
}

/** Records in one of the given statuses, oldest first. */
export async function listByStatus(statuses: readonly OutboxStatus[]): Promise<OutboxRecord[]> {
  const wanted = new Set(statuses);
  const all = await listAll();
  return all.filter((r) => wanted.has(r.status));
}

/** Operations still owed to the server (pending, retrying, or stuck sending). */
export async function listOutstanding(): Promise<OutboxRecord[]> {
  return listByStatus(['PENDING', 'SENDING', 'FAILED']);
}

/** Remove synced rows older than `olderThanMs` so the store does not grow forever. */
export async function pruneSynced(olderThanMs = 24 * 60 * 60 * 1000): Promise<number> {
  const db = await openOfflineDb();
  const cutoff = Date.now() - olderThanMs;
  const all = await db.getAll(OUTBOX_STORE);
  let removed = 0;
  for (const record of all) {
    if (record.status === 'SYNCED' && record.updatedAt < cutoff) {
      await db.delete(OUTBOX_STORE, record.id);
      removed += 1;
    }
  }
  return removed;
}

/** Drop one record — used when a human resolves a conflict by discarding it. */
export async function discard(id: string): Promise<void> {
  const db = await openOfflineDb();
  await db.delete(OUTBOX_STORE, id);
}

/** Put a conflicted record back in the queue after a human fixed the cause. */
export async function retryNow(id: string): Promise<void> {
  const db = await openOfflineDb();
  const record = await db.get(OUTBOX_STORE, id);
  if (record === undefined) return;
  await db.put(OUTBOX_STORE, {
    ...record,
    status: 'PENDING',
    nextAttemptAt: Date.now(),
    lastError: null,
    updatedAt: Date.now(),
  });
}

async function writeStatus(
  record: OutboxRecord,
  status: OutboxStatus,
  patch: Partial<Pick<OutboxRecord, 'lastError' | 'lastStatus' | 'attempts' | 'nextAttemptAt'>> = {},
): Promise<void> {
  const db = await openOfflineDb();
  await db.put(OUTBOX_STORE, { ...record, ...patch, status, updatedAt: Date.now() });
}

export interface ReplayReport {
  readonly attempted: number;
  readonly synced: number;
  readonly failed: number;
  readonly conflicted: number;
  /** True when the run stopped early because the network is clearly down. */
  readonly stoppedEarly: boolean;
}

/**
 * Replay the queue once, FIFO.
 *
 * Stops at the first retryable failure: if the network is down, hammering the
 * remaining fifty records only drains the battery, and out-of-order replay of
 * dependent operations (create order → add items) corrupts the bill.
 */
export async function replay(transport: OutboxTransport, now = Date.now()): Promise<ReplayReport> {
  const due = (await listOutstanding()).filter((r) => r.nextAttemptAt <= now);
  let attempted = 0;
  let synced = 0;
  let failed = 0;
  let conflicted = 0;
  let stoppedEarly = false;

  for (const record of due) {
    attempted += 1;
    await writeStatus(record, 'SENDING');
    let outcome;
    try {
      outcome = await transport(record);
    } catch (error: unknown) {
      outcome = {
        ok: false as const,
        retryable: true,
        status: null,
        message: error instanceof Error ? error.message : 'Lỗi không xác định',
      };
    }

    if (outcome.ok) {
      await writeStatus(record, 'SYNCED', { lastError: null, lastStatus: 200 });
      synced += 1;
      continue;
    }

    const attempts = record.attempts + 1;
    if (outcome.retryable) {
      await writeStatus(record, 'FAILED', {
        attempts,
        lastError: outcome.message,
        lastStatus: outcome.status,
        nextAttemptAt: Date.now() + backoffFor(attempts),
      });
      failed += 1;
      stoppedEarly = true;
      break;
    }

    await writeStatus(record, 'CONFLICT', {
      attempts,
      lastError: outcome.message,
      lastStatus: outcome.status,
    });
    conflicted += 1;
  }

  if (synced > 0) {
    const db = await openOfflineDb();
    await db.put(META_STORE, Date.now(), LAST_SYNCED_KEY);
  }

  return { attempted, synced, failed, conflicted, stoppedEarly };
}

/** Epoch ms of the last successful replay. */
export async function lastSyncedAt(): Promise<number | null> {
  const db = await openOfflineDb();
  const value = await db.get(META_STORE, LAST_SYNCED_KEY);
  return typeof value === 'number' ? value : null;
}

/**
 * Compute what the sync pill should show.
 *
 * Precedence: a conflict outranks everything (a human must act), then a hard
 * error, then in-flight, then merely-queued, then plain online/offline.
 */
export function deriveSyncState(input: {
  readonly online: boolean;
  readonly pendingCount: number;
  readonly sendingCount: number;
  readonly failedCount: number;
  readonly conflictCount: number;
}): SyncState {
  if (input.conflictCount > 0) return 'CONFLICT';
  if (input.failedCount > 0) return 'ERROR';
  if (input.sendingCount > 0) return 'SYNCING';
  if (input.pendingCount > 0) return input.online ? 'SYNCING' : 'UNSYNCED';
  return input.online ? 'ONLINE' : 'OFFLINE';
}

/** Read the whole queue and summarise it for the UI. */
export async function snapshot(online: boolean): Promise<SyncSnapshot> {
  if (!isIndexedDbAvailable()) {
    return {
      state: online ? 'ONLINE' : 'OFFLINE',
      pendingCount: 0,
      failedCount: 0,
      conflictCount: 0,
      lastSyncedAt: null,
      online,
    };
  }
  const all = await listAll();
  const pendingCount = all.filter((r) => r.status === 'PENDING').length;
  const sendingCount = all.filter((r) => r.status === 'SENDING').length;
  const failedCount = all.filter((r) => r.status === 'FAILED').length;
  const conflictCount = all.filter((r) => r.status === 'CONFLICT').length;
  return {
    state: deriveSyncState({ online, pendingCount, sendingCount, failedCount, conflictCount }),
    pendingCount: pendingCount + sendingCount,
    failedCount,
    conflictCount,
    lastSyncedAt: await lastSyncedAt(),
    online,
  };
}

/** Vietnamese label for the pill. The staff-facing surface is Vietnamese only. */
export function syncStateLabel(state: SyncState): string {
  switch (state) {
    case 'ONLINE':
      return 'Trực tuyến';
    case 'OFFLINE':
      return 'Ngoại tuyến';
    case 'SYNCING':
      return 'Đang đồng bộ';
    case 'UNSYNCED':
      return 'Chưa đồng bộ';
    case 'ERROR':
      return 'Lỗi đồng bộ';
    case 'CONFLICT':
      return 'Xung đột';
  }
}
